// packages/web-ui/src/panels/chat-panel/MessageList.tsx
//
// W2-S5 AC #4 — Virtualised message list using `@tanstack/react-virtual`.
// Auto-scrolls to bottom on mount + new messages, but only when the user is
// already near the bottom (Pitfall #3: do NOT yank focus when scrolled up).
// "Jump to latest" overlay button restores the bottom-anchor.
//
// W2-S10b Task 4.5 (AC #6) — `<ChatThread>` ARIA contract:
//   role="log" + aria-live="polite" + aria-relevant="additions" — already on.
//   aria-busy={streamingMessageId !== null} added so SR users know the agent
//   is mid-response; per-message `role="article"` + `aria-labelledby` is
//   delegated to `<MessageBubble>` (owned by W2-S6-integration; out of this
//   pass's scope).

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ChatPanelMessage } from '../../state/chat.js';
import type { InlineCardEnvelope } from '../../state/inline-cards.js';
import type { InlineCardHandlers } from '../../components/inline-cards/index.js';
import { MessageBubble } from './MessageBubble.js';
import { useReducedMotion } from '../../AppShell/useReducedMotion.js';

const NEAR_BOTTOM_THRESHOLD = 64;

export interface MessageListProps {
  messages: ChatPanelMessage[];
  streamingMessageId: string | null;
  onRetryToolCall?: (callId: string) => void;
  onRerunToolCall?: (callId: string) => void;
  openInSyside?: (path: string) => void;
  /**
   * Click handler for KB source citation pills inside the assistant body.
   * Forwarded as-is to each `<MessageBubble>`; `ChatPanel` binds it to
   * `useOpenResource('files', filePath)` so the source file opens in a tab.
   */
  onOpenSource?: (filePath: string, elementName?: string) => void;
  /** Optional bottom-of-list slot — used by `<ChatPanel>` to inject the thinking indicator. */
  footer?: JSX.Element | null;
  /**
   * W4 fix-pass HIGH-F — per-message inline-card envelopes, indexed by
   * message id. The list forwards `inlineCardsByMessageId[message.id]`
   * (or undefined) to each `<MessageBubble>`. Empty / undefined renders
   * the bubble's empty `inline-cards-slot` placeholder.
   */
  inlineCardsByMessageId?: Record<string, InlineCardEnvelope[]>;
  /**
   * W4 fix-pass HIGH-F — composed handlers for the four inline-card
   * kinds. Forwarded straight through to each `<MessageBubble>`. The
   * caller (`<ChatPanel>`) builds this object once per render so the
   * bubble identity is preserved between message updates.
   */
  inlineCardHandlers?: InlineCardHandlers;
  /** Honour `prefers-reduced-motion: reduce`. Forwarded to `<MessageBubble>`. */
  reducedMotion?: boolean;
  /**
   * Per-message delete handler. Forwarded to each `<MessageBubble>`; when
   * undefined the bubble renders no delete affordance.
   */
  onDeleteMessage?: (messageId: string) => void;
}

export function MessageList(props: MessageListProps): JSX.Element {
  const { messages, streamingMessageId, footer } = props;
  // W2-S10b Task 4.4 — switched from a local `prefers-reduced-motion` helper
  // to the canonical AppShell hook so all panels share one motion source.
  const reducedMotion = useReducedMotion();
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastCountRef = useRef(messages.length);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const wasNearBottomRef = useRef(true);

  const rowVirtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 96,
    overscan: 4,
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  // Track whether the user is near the bottom (pre-update sample).
  const checkNearBottom = useCallback((): boolean => {
    const el = scrollRef.current;
    if (el === null) return true;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    return remaining <= NEAR_BOTTOM_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior) => {
      const el = scrollRef.current;
      if (el === null) return;
      const eff: ScrollBehavior = reducedMotion ? 'auto' : behavior;
      // jsdom's HTMLElement lacks scrollTo by default; guard so unit tests
      // don't blow up in the layout-effect mount path.
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: el.scrollHeight, behavior: eff });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    },
    [reducedMotion],
  );

  // On mount: instant scroll to last. Intentionally mount-only.
  useLayoutEffect(() => {
    scrollToBottom('auto');
  }, [scrollToBottom]);

  // On new message: respect prior near-bottom state.
  useEffect(() => {
    if (messages.length > lastCountRef.current) {
      if (wasNearBottomRef.current) {
        scrollToBottom('smooth');
        setShowJumpToLatest(false);
      } else {
        setShowJumpToLatest(true);
      }
    }
    lastCountRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Token stream updates the LAST message's content — auto-scroll only if
  // we're near bottom; don't yank.
  useEffect(() => {
    if (streamingMessageId === null) return;
    if (wasNearBottomRef.current) {
      scrollToBottom('auto');
    }
  }, [streamingMessageId, messages, scrollToBottom]);

  const onScroll = useCallback(() => {
    const near = checkNearBottom();
    wasNearBottomRef.current = near;
    if (near) setShowJumpToLatest(false);
  }, [checkNearBottom]);

  // Demo-playback fix: useMemo([rowVirtualizer]) is stale because the virtualizer
  // instance reference is stable across renders, so getVirtualItems() was only
  // called once at mount when count=0. Calling it inline lets each render see
  // the current range.
  const items = rowVirtualizer.getVirtualItems();

  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      data-testid="message-list"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-busy={streamingMessageId !== null}
      className="relative flex-1 overflow-y-auto px-4 py-3"
    >
      <div
        style={{ height: rowVirtualizer.getTotalSize(), position: 'relative', width: '100%' }}
      >
        {items.map((row) => {
          const msg = messages[row.index];
          if (msg === undefined) return null;
          return (
            <div
              key={msg.id}
              ref={(el) => {
                if (el !== null) rowVirtualizer.measureElement(el);
              }}
              data-index={row.index}
              data-testid="message-row"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${row.start}px)`,
              }}
            >
              <div className="flex flex-col gap-4 pb-4">
                <MessageBubble
                  message={msg}
                  isStreaming={streamingMessageId === msg.id}
                  onRetryToolCall={props.onRetryToolCall}
                  onRerunToolCall={props.onRerunToolCall}
                  openInSyside={props.openInSyside}
                  onOpenSource={props.onOpenSource}
                  inlineCards={props.inlineCardsByMessageId?.[msg.id]}
                  inlineCardHandlers={props.inlineCardHandlers}
                  reducedMotion={props.reducedMotion}
                  onDelete={props.onDeleteMessage}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer slot — typically a <ThinkingIndicator>. Lives outside the
          virtualised row container so it always renders. */}
      {footer !== null && footer !== undefined ? (
        <div data-testid="message-list-footer">{footer}</div>
      ) : null}

      {showJumpToLatest ? (
        <button
          type="button"
          data-testid="jump-to-latest"
          onClick={() => scrollToBottom('smooth')}
          className="absolute bottom-4 right-4 rounded-full border border-taupe-500 bg-cream-100 px-3 py-1 text-xs shadow-sm hover:bg-cream-200"
        >
          Jump to latest ↓
        </button>
      ) : null}
    </div>
  );
}
